import './BigDeviceList.scss';
import React, { useState } from 'react';
import Masonry from 'react-masonry-css';
import {BigDisplay, DEFAULT_PLAYBACK_SETTINGS, MIN_PLAYBACK_INTERVAL_MINUTES, PlaybackSettings} from '../../types/BigDisplay';
import { ImageSlider } from '../ImageSlider';
import { imageIcon } from '../../utils/images';
import { Upload, Play, Pause, RotateCcw, Settings, Smartphone, Monitor, Trash2, X, Wifi, Activity } from 'lucide-react';
import BatteryIcon_V from '../../assets/icons/BatteryIcon_V.svg';
import BatteryIcon_IV from '../../assets/icons/BatteryIcon_IV.svg';
import BatteryIcon_III from '../../assets/icons/BatteryIcon_III.svg';
import BatteryIcon_II from '../../assets/icons/BatteryIcon_II.svg';
import BatteryIcon_I from '../../assets/icons/BatteryIcon_I.svg';

const formatLastSeen = (value?: string | null): string => {
  if (!value) {
    return 'never';
  }

  const updatedAt = new Date(value).getTime();
  if (Number.isNaN(updatedAt)) {
    return 'never';
  }

  const seconds = Math.max(0, Math.floor((Date.now() - updatedAt) / 1000));
  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  return `${Math.floor(hours / 24)}d ago`;
};

interface BigDeviceListProps {
  displays: BigDisplay[];
  onImageIconClick?: (displayId: number) => void;
  onUploadClick?: (displayId: number) => void;
  onOrientationChange?: (displayId: number, orientation: 'portrait' | 'landscape') => void;
  onDeleteClick?: (displayId: number) => void;
  onPausePlay?: (displayId: number, paused: boolean) => void;
  onRestart?: (displayId: number) => void;
  onCheckOnline?: (displayId: number) => void | Promise<void>;
  checkingOnlineId?: number | null;
  onSettingsChange?: (displayId: number, patch: Partial<PlaybackSettings>) => void;
  defaultColumns?: number;
}

export const BigDeviceList: React.FC<BigDeviceListProps> = ({
  displays,
  onImageIconClick,
  onUploadClick,
  onOrientationChange,
  onDeleteClick,
  onPausePlay,
  onRestart,
  onCheckOnline,
  checkingOnlineId,
  onSettingsChange,
  defaultColumns = 2
}) => {
  const [openSettingsId, setOpenSettingsId] = useState<number | null>(null);
  const [intervalDraftMinutes, setIntervalDraftMinutes] = useState<number>(MIN_PLAYBACK_INTERVAL_MINUTES);
  const [shuffleDraft, setShuffleDraft] = useState<boolean>(false);
  const [repeatDraft, setRepeatDraft] = useState<boolean>(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const intervalDraftIsInvalid = !Number.isFinite(intervalDraftMinutes) || intervalDraftMinutes < MIN_PLAYBACK_INTERVAL_MINUTES;

  const handleGearClick = (display: BigDisplay) => {
    const ps = { ...DEFAULT_PLAYBACK_SETTINGS, ...(display.playback_settings ?? {}) };
    setIntervalDraftMinutes(Math.max(MIN_PLAYBACK_INTERVAL_MINUTES, Math.ceil(ps.interval / 60)));
    setShuffleDraft(ps.shuffle);
    setRepeatDraft(ps.repeat);
    setOpenSettingsId(prev => (prev === display.id ? null : display.id));
  };

  const saveDraftSettings = async (displayId: number) => {
    if (intervalDraftIsInvalid) {
      return;
    }

    setSettingsSaving(true);
    try {
      await onSettingsChange?.(displayId, {
        interval: intervalDraftMinutes * 60,
        shuffle: shuffleDraft,
        repeat: repeatDraft,
      });
      setOpenSettingsId(null);
    } finally {
      setSettingsSaving(false);
    }
  };
  // Define breakpoints for responsive columns
  const breakpointColumnsObj = {
    default: defaultColumns,
    1200: Math.max(defaultColumns - 1, 2),
    900: Math.max(defaultColumns - 2, 1),
    600: 1
  };
  const safeDisplays: BigDisplay[] = (displays ?? []).map(d => ({
    ...d,
    current_image: d.current_image ?? [],
  }));

  return (
    <div className="big-device-list">
      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="big-device-list__masonry-grid"
        columnClassName="big-device-list__masonry-grid_column"
      >
        {safeDisplays.map((display) => {
          const ps = { ...DEFAULT_PLAYBACK_SETTINGS, ...(display.playback_settings ?? {}) };
          const settingsOpen = openSettingsId === display.id;
          const checkingConnection = checkingOnlineId === display.id;
          return (
            <div key={display.id} className="big-device-list__card">
              <div className="big-device-list__label">
                {/* <p className="big-device-list__label--position">ID: {display.id}</p> */}
                <p className="big-device-list__label--position">{display.android_id}</p>
                <div className="big-device-list__label-status">
                  <p className="big-device-list__label--images">Images: {display.current_image.length}</p>
                  <p className="big-device-list__label--seen">Seen: {formatLastSeen(display.battery_updated_at)}</p>

                  {display.battery_level != null && (
                    <div className="big-device-list__Battery">
                      <img
                        src={
                          display.battery_level > 80 ? BatteryIcon_V :
                          display.battery_level > 60 ? BatteryIcon_IV :
                          display.battery_level > 40 ? BatteryIcon_III :
                          display.battery_level > 20 ? BatteryIcon_II :
                          BatteryIcon_I
                        }
                        alt={`Battery ${display.battery_level}%`}
                        title={`Battery: ${display.battery_level}%`}
                      />
                      <span className="big-device-list__Battery-text">{display.battery_level}%</span>
                    </div>
                  )}

                  {display.wifi_rssi != null && (
                    <div className="big-device-list__Wifi" title={`WiFi: ${display.wifi_rssi} dBm`}>
                      <Wifi
                        size={18}
                        color={
                          display.wifi_rssi >= -50 ? '#4caf50' :
                          display.wifi_rssi >= -70 ? '#ff9800' :
                          '#f44336'
                        }
                      />
                    </div>
                  )}
                  </div>

              </div>
              <div className="big-device-list__preview">
                <ImageSlider
                  images={display.current_image}
                  displayId={display.id}
                />
                <div className="big-device-list__buttons">
                  <button
                    className="big-device-list__button"
                    onClick={() => onUploadClick?.(display.id)}
                    title="Upload media"
                  >
                    <Upload size={16} />
                  </button>
                  {onImageIconClick && (
                    <button
                      className="big-device-list__button"
                      onClick={() => onImageIconClick(display.id)}
                    >
                      <img src={imageIcon} alt="Image" />
                    </button>
                  )}
                  {onPausePlay && (
                    <button
                      className={`big-device-list__button${ps.paused ? ' big-device-list__button--active' : ''}`}
                      onClick={() => onPausePlay(display.id, !ps.paused)}
                      title={ps.paused ? 'Resume' : 'Pause'}
                    >
                      {ps.paused ? <Play size={16} /> : <Pause size={16} />}
                    </button>
                  )}
                  {onRestart && (
                    <button
                      className="big-device-list__button"
                      onClick={() => onRestart(display.id)}
                      title="Restart playback"
                    >
                      <RotateCcw size={16} />
                    </button>
                  )}
                  {onSettingsChange && (
                    <button
                      className={`big-device-list__button${settingsOpen ? ' big-device-list__button--active' : ''}`}
                      onClick={() => handleGearClick(display)}
                      title="Playback settings"
                    >
                      <Settings size={16} />
                    </button>
                  )}
                  <div className="big-device-list__orientation-controls">
                    <button
                      className={`big-device-list__orientation-btn ${display.orientation === 'portrait' ? 'active' : ''}`}
                      onClick={() => onOrientationChange?.(display.id, 'portrait')}
                      title="Portrait"
                    >
                      <Smartphone size={16} />
                    </button>
                    <button
                      className={`big-device-list__orientation-btn ${display.orientation === 'landscape' ? 'active' : ''}`}
                      onClick={() => onOrientationChange?.(display.id, 'landscape')}
                      title="Landscape"
                    >
                      <Monitor size={16} />
                    </button>
                  </div>
                  {onDeleteClick && (
                    <button
                      className="big-device-list__button big-device-list__button--delete"
                      onClick={() => onDeleteClick(display.id)}
                      title="Remove display"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
              {settingsOpen && onSettingsChange && (
                <div className="big-device-list__settings-panel">
                  <div className="big-device-list__settings-header">
                    <span className="big-device-list__settings-title">Playback Settings</span>
                    <button
                      className="big-device-list__settings-close"
                      onClick={() => setOpenSettingsId(null)}
                      title="Close"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="big-device-list__settings-row">
                    <p className="big-device-list__settings-label">{display.display_type}</p>
                  </div>
                  {onCheckOnline && (
                    <div className="big-device-list__settings-row">
                      <label className="big-device-list__settings-label">Connection</label>
                      <button
                        className={`big-device-list__settings-check${checkingConnection ? ' big-device-list__settings-check--checking' : ''}`}
                        onClick={() => onCheckOnline(display.id)}
                        disabled={checkingConnection}
                      >
                        <Activity size={14} />
                        {checkingConnection ? 'Checking...' : 'Check connection'}
                      </button>
                    </div>
                  )}
                  <div className="big-device-list__settings-row">
                    <label className="big-device-list__settings-label">Interval (minutes)</label>
                    <input
                      className="big-device-list__settings-input"
                      type="number"
                      min={MIN_PLAYBACK_INTERVAL_MINUTES}
                      step={1}
                      value={intervalDraftMinutes}
                      aria-invalid={intervalDraftIsInvalid}
                      aria-describedby={`display-${display.id}-interval-error`}
                      onChange={e => {
                        const nextValue = Number(e.target.value);
                        if (!Number.isNaN(nextValue)) {
                          setIntervalDraftMinutes(nextValue);
                        }
                      }}
                    />
                  </div>
                  {intervalDraftIsInvalid && (
                    <p
                      id={`display-${display.id}-interval-error`}
                      className="big-device-list__settings-error"
                    >
                      Minimum interval is {MIN_PLAYBACK_INTERVAL_MINUTES} minutes.
                    </p>
                  )}
                  <div className="big-device-list__settings-row">
                    <label className="big-device-list__settings-label">Shuffle</label>
                    <button
                      className={`big-device-list__settings-toggle${shuffleDraft ? ' big-device-list__settings-toggle--on' : ''}`}
                      onClick={() => setShuffleDraft(v => !v)}
                    >
                      {shuffleDraft ? 'On' : 'Off'}
                    </button>
                  </div>
                  <div className="big-device-list__settings-row">
                    <label className="big-device-list__settings-label">Repeat</label>
                    <button
                      className={`big-device-list__settings-toggle${repeatDraft ? ' big-device-list__settings-toggle--on' : ''}`}
                      onClick={() => setRepeatDraft(v => !v)}
                    >
                      {repeatDraft ? 'On' : 'Off'}
                    </button>
                  </div>
                  <div className="big-device-list__settings-actions">
                    <button
                      className="big-device-list__settings-save"
                      onClick={() => saveDraftSettings(display.id)}
                      disabled={settingsSaving || intervalDraftIsInvalid}
                    >
                      {settingsSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </Masonry>

      {displays.length === 0 && (
        <div className="big-device-list__empty">
          <p>No displays found.</p>
        </div>
      )}
    </div>
  );
};
